import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Briefcase } from "lucide-react";
import { API } from "@/lib/site";

const inputCls =
  "w-full bg-[#0b1e4b]/40 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none transition-colors";
const labelCls = "block text-[11px] font-mono uppercase text-slate-300 mb-1.5";
const fileCls =
  "w-full text-xs text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-mono file:bg-white/10 file:text-white hover:file:bg-white/20 cursor-pointer";

function Honeypot() {
  return (
    <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true">
      <label htmlFor="hp-website">Website</label>
      <input type="text" id="hp-website" name="website" tabIndex={-1} autoComplete="off" />
    </div>
  );
}

function PrivacyField({ id, accent, onOpen }) {
  return (
    <div className="flex items-start gap-3 pt-1">
      <input
        type="checkbox"
        id={id}
        name="privacy"
        required
        data-testid={`${id}-checkbox`}
        className="mt-0.5 w-4 h-4 rounded border-white/20 bg-black/40 cursor-pointer"
        style={{ accentColor: accent }}
      />
      <label htmlFor={id} className="text-[11px] text-slate-400 leading-relaxed cursor-pointer">
        Ho letto e accetto l'
        <button type="button" onClick={onOpen} className="underline hover:text-white transition-colors" data-testid={`${id}-open-privacy`}>
          informativa privacy
        </button>{" "}
        e acconsento al trattamento dei dati per essere ricontattato. *
      </label>
    </div>
  );
}

function useSubmit(endpoint, fileField, successMsg) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    fd.set("privacy", form.privacy.checked ? "true" : "false");
    const fileInput = form.querySelector(`input[name="${fileField}"]`);
    if (!fileInput?.files?.length) fd.delete(fileField);
    setLoading(true);
    try {
      await axios.post(`${API}/${endpoint}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(successMsg.title, { description: successMsg.body });
      form.reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Invio non riuscito", {
        description: typeof detail === "string" ? detail : "Riprova tra qualche istante.",
      });
    } finally {
      setLoading(false);
    }
  };
  return { loading, submit };
}

export function ContactForm({ onOpenPrivacy }) {
  const { loading, submit } = useSubmit("contact", "allegato", {
    title: "Richiesta inviata con successo",
    body: "Ti ricontatteremo al più presto. Grazie per aver scelto Supernova.",
  });
  const focus = "focus:border-[#ff6a00]";

  return (
    <div id="contattaci" className="p-8 sm:p-10 rounded-3xl bg-white/[0.03] border border-white/15 backdrop-blur-md relative overflow-hidden scroll-mt-24" data-testid="contact-section">
      <div className="absolute top-0 right-0 w-40 h-40 bg-[#ff6a00]/10 rounded-full blur-2xl pointer-events-none"></div>
      <div className="mb-8">
        <span className="font-mono text-xs text-[#ff6a00] uppercase tracking-widest block mb-1">FUNZIONE 01</span>
        <h2 className="font-display text-3xl uppercase text-white">CONTATTACI</h2>
        <p className="text-xs text-slate-400 mt-1">Richiedi informazioni o una verifica senza impegno per le tue utenze.</p>
      </div>

      <form className="space-y-4" onSubmit={submit} data-testid="contact-form">
        <Honeypot />
        <div>
          <label htmlFor="contact-nome" className={labelCls}>Nome e Cognome *</label>
          <input id="contact-nome" name="nome" type="text" required maxLength={120} placeholder="Mario Rossi" data-testid="contact-name-input" className={`${inputCls} ${focus}`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="contact-telefono" className={labelCls}>Telefono *</label>
            <input id="contact-telefono" name="telefono" type="tel" required maxLength={30} placeholder="+39 333 0000000" data-testid="contact-phone-input" className={`${inputCls} ${focus}`} />
          </div>
          <div>
            <label htmlFor="contact-email" className={labelCls}>Email *</label>
            <input id="contact-email" name="email" type="email" required maxLength={120} placeholder="mario@esempio.it" data-testid="contact-email-input" className={`${inputCls} ${focus}`} />
          </div>
        </div>
        <div>
          <label htmlFor="contact-motivo" className={labelCls}>Motivo Richiesta *</label>
          <select id="contact-motivo" name="motivo" required defaultValue="luce" data-testid="contact-motivo-select" className={`w-full bg-[#101c42] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none ${focus} transition-colors`}>
            <option value="luce">Consulenza Luce</option>
            <option value="gas">Consulenza Gas</option>
            <option value="fotovoltaico">Impianto Fotovoltaico</option>
            <option value="segnalazione">Segnalazione / Altro</option>
          </select>
        </div>
        <div>
          <label htmlFor="contact-messaggio" className={labelCls}>Messaggio</label>
          <textarea id="contact-messaggio" name="messaggio" rows={3} maxLength={2000} placeholder="Descrivi brevemente la tua richiesta..." data-testid="contact-message-input" className={`${inputCls} ${focus}`}></textarea>
        </div>
        <div>
          <label htmlFor="contact-allegato" className={labelCls}>Allegato Bolletta (Opzionale)</label>
          <input id="contact-allegato" name="allegato" type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="contact-attachment-input" className={fileCls} />
        </div>
        <PrivacyField id="contact-privacy" accent="#ff6a00" onOpen={onOpenPrivacy} />
        <button
          type="submit"
          disabled={loading}
          data-testid="contact-submit-button"
          className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#ff6a00] to-[#f43f5e] hover:brightness-110 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-widest py-3.5 rounded-lg transition shadow-lg shadow-[#ff6a00]/25"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? "Invio in corso..." : "Invia Richiesta"}
        </button>
      </form>
    </div>
  );
}

export function CareersForm({ onOpenPrivacy }) {
  const { loading, submit } = useSubmit("careers", "cv", {
    title: "Candidatura inviata con successo",
    body: "La tua candidatura è arrivata al nostro team. A presto!",
  });
  const focus = "focus:border-purple-400";

  return (
    <div id="lavora" className="p-8 sm:p-10 rounded-3xl bg-white/[0.03] border border-white/15 backdrop-blur-md relative overflow-hidden scroll-mt-24" data-testid="careers-section">
      <div className="absolute top-0 right-0 w-40 h-40 bg-purple-600/10 rounded-full blur-2xl pointer-events-none"></div>
      <div className="mb-8">
        <span className="font-mono text-xs text-purple-400 uppercase tracking-widest block mb-1">FUNZIONE 02</span>
        <h2 className="font-display text-3xl uppercase text-white">LAVORA CON NOI</h2>
        <p className="text-xs text-slate-400 mt-1">
          Entra nella rete Supernova Energy Network. Cerchiamo persone motivate e collaboratori per sviluppare insieme nuove opportunità nel settore energia e gas.
        </p>
      </div>

      <form className="space-y-4" onSubmit={submit} data-testid="careers-form">
        <Honeypot />
        <div>
          <label htmlFor="careers-nome" className={labelCls}>Nome e Cognome *</label>
          <input id="careers-nome" name="nome" type="text" required maxLength={120} placeholder="Laura Bianchi" data-testid="careers-name-input" className={`${inputCls} ${focus}`} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="careers-telefono" className={labelCls}>Telefono *</label>
            <input id="careers-telefono" name="telefono" type="tel" required maxLength={30} placeholder="+39 340 0000000" data-testid="careers-phone-input" className={`${inputCls} ${focus}`} />
          </div>
          <div>
            <label htmlFor="careers-email" className={labelCls}>Email *</label>
            <input id="careers-email" name="email" type="email" required maxLength={120} placeholder="laura@esempio.it" data-testid="careers-email-input" className={`${inputCls} ${focus}`} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="careers-citta" className={labelCls}>Città / Provincia *</label>
            <input id="careers-citta" name="citta" type="text" required maxLength={120} placeholder="Milano (MI)" data-testid="careers-city-input" className={`${inputCls} ${focus}`} />
          </div>
          <div>
            <label htmlFor="careers-esperienza" className={labelCls}>Esperienza nel settore *</label>
            <select id="careers-esperienza" name="esperienza" required defaultValue="si" data-testid="careers-experience-select" className={`w-full bg-[#101c42] border border-white/15 rounded-lg px-4 py-3 text-sm text-white focus:outline-none ${focus} transition-colors`}>
              <option value="si">Sì, ho esperienza</option>
              <option value="no">No, prima esperienza</option>
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="careers-presentazione" className={labelCls}>Breve Presentazione</label>
          <textarea id="careers-presentazione" name="presentazione" rows={3} maxLength={2000} placeholder="Parlaci delle tue motivazioni o del tuo percorso..." data-testid="careers-message-input" className={`${inputCls} ${focus}`}></textarea>
        </div>
        <div>
          <label htmlFor="careers-cv" className={labelCls}>Curriculum Vitae (Opzionale)</label>
          <input id="careers-cv" name="cv" type="file" accept=".pdf,.jpg,.jpeg,.png" data-testid="careers-cv-input" className={fileCls} />
        </div>
        <PrivacyField id="careers-privacy" accent="#c084fc" onOpen={onOpenPrivacy} />
        <button
          type="submit"
          disabled={loading}
          data-testid="careers-submit-button"
          className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-[#f43f5e] hover:brightness-110 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-widest py-3.5 rounded-lg transition shadow-lg shadow-purple-600/25"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Briefcase className="w-4 h-4" />}
          {loading ? "Invio in corso..." : "Invia Candidatura"}
        </button>
      </form>
    </div>
  );
}

export function FormsSection({ onOpenPrivacy }) {
  return (
    <section className="py-24 px-6 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <ContactForm onOpenPrivacy={onOpenPrivacy} />
        <CareersForm onOpenPrivacy={onOpenPrivacy} />
      </div>
    </section>
  );
}
